import { Editor as TinyMCEEditor } from '@tinymce/tinymce-react';
import { useParams } from 'react-router';
import { Box } from '@mui/material';
import { kebabCase } from 'lodash/fp';
import { UPLOAD_URL } from '@/config';
import { uploadFileRequest } from '@/api/files';
import {
  getEmbedDialogConfig,
  getGameRecapUrl,
  sanitizeScripts,
} from './utils';
import styles from './styles';

type EditorComponentProps = {
  id?: string;
  value: string;
  onChange: (content: string) => void;
  onSubmit?: (content: string, title?: string) => void;
  media?: boolean;
  placeholder?: string;
};

/**
 * Editor component for rendering a TinyMCE editor.
 *
 * @param {EditorComponentProps} props - The component props.
 * @returns {JSX.Element} The rendered TinyMCE editor component.
 */
const Editor = ({
  id = 'tinymce-editor',
  value,
  onChange,
  onSubmit,
  media = false,
  placeholder = '',
  ...rest
}: EditorComponentProps) => {
  const { propertyId } = useParams<{ propertyId: string }>();

  return (
    <Box sx={styles.editorContainer}>
      <TinyMCEEditor
        id={id}
        licenseKey="gpl"
        tinymceScriptSrc="/tinymce/tinymce.min.js"
        value={value}
        onEditorChange={onChange}
        {...rest}
        init={{
          height: 200,
          min_height: 200,
          max_height: 600,
          menubar: false,
          statusbar: false,
          placeholder,
          images_upload_handler: async (blobInfo: {
            blob: () => Blob;
            filename: () => string;
          }) => {
            if (!propertyId) {
              return Promise.reject(new Error('Property ID is required'));
            }

            try {
              const formData = new FormData();
              formData.append(
                'file',
                blobInfo.blob(),
                kebabCase(blobInfo.filename()),
              );

              const response = await uploadFileRequest({
                formData,
                propertyId,
              });

              return `${UPLOAD_URL}/${response?.fullPath}`;
            } catch (error) {
              return Promise.reject(new Error('Error uploading file'));
            }
          },

          ...(media && {
            extended_valid_elements:
              'glomex-player,script[type<application/ld+json>]',
          }),

          plugins: [
            'autoresize',
            'anchor',
            'autolink',
            'image',
            'link',
            'lists',
            ...(media ? ['media'] : []),
          ],

          toolbar: `h3 | bold italic underline | numlist bullist | link ${media ? 'image media embed' : ''} | GameRecap`,

          toolbar_mode: 'wrap',

          ...(media && {
            setup: (editor: any) => {
              const embedDialogConfig = getEmbedDialogConfig(editor, onSubmit);

              editor.ui.registry.addButton('embed', {
                icon: 'embed-page',
                tooltip: 'Insert media embed',
                onAction: () => editor.windowManager.open(embedDialogConfig),
              });

              editor.on('BeforeSetContent', sanitizeScripts);

              const gameRecapUrl = getGameRecapUrl(editor);
              editor.ui.registry.addButton('GameRecap', {
                tooltip: 'Game Recap',
                icon: 'new-document',
                onAction: () => editor.windowManager.open(gameRecapUrl),
              });
            },
          }),
        }}
      />
    </Box>
  );
};

export default Editor;
